<ng-template #typeLabel let-atype>
  @if (atype.name === '') {
    {{ atype.assetClass | i18nextCap }} ({{ 'generic' | i18nextCap }})
  } @else {
    {{ atype.name }}
  }
</ng-template>

@if (choosingType) {
  <div class="type-step">
    <span class="type-step-title">{{ 'chooseAssetType' | i18nextCap }}</span>
    @for (group of typeGroups; track group.assetClass) {
      <span class="type-group-title">{{ group.assetClass | i18nextCap }}</span>
      <div class="type-cards">
        @for (atype of group.types; track atype.id) {
          <button
            type="button"
            class="type-card"
            [class.current]="atype.id === chosenType?.id"
            (click)="chooseType(atype)"
          >
            <span class="type-card-text">
              <span class="type-card-name">{{
                atype.name === '' ? (atype.assetClass | i18nextCap) : atype.name
              }}</span>
              <span class="type-card-meta">
                @if (atype.name === '') {
                  {{ 'genericType' | i18nextCap }}
                } @else if (atype.fieldCount === 0) {
                  {{ 'noCustomFields' | i18nextCap }}
                } @else if (atype.fieldCount === 1) {
                  {{ 'oneCustomField' | i18nextCap }}
                } @else {
                  {{ atype.fieldCount }} {{ 'customFields' | i18nextCap }}
                }
              </span>
            </span>
            <mat-icon>{{
              atype.id === chosenType?.id ? 'check_circle' : 'chevron_right'
            }}</mat-icon>
          </button>
        }
      </div>
    }
  </div>
} @else {
  <fo-sectioned-form [showIndex]="showSectionIndex">
    <fo-form-section
      [title]="'general' | i18nextCap"
      [description]="'generalDescription' | i18nextCap"
      icon="badge"
      sectionId="general"
      [locked]="isLocked('general')"
      [status]="sectionStatus('general')"
      [errorCount]="sectionErrors('general')"
      [showNext]="hasNext('general')"
      (next)="openNext('general')"
    >
      <div class="section-body" [formGroup]="baseFormGroup">
        @if (typeFirst) {
          <mat-form-field>
            <mat-label>{{ 'assetType' | i18nextCap }}</mat-label>
            <input
              matInput
              readonly
              [value]="
                chosenType?.name === ''
                  ? (chosenType.assetClass | i18nextCap) +
                    ' (' +
                    ('generic' | i18nextCap) +
                    ')'
                  : chosenType?.name
              "
            />
            <button
              mat-button
              matSuffix
              color="primary"
              type="button"
              class="change-type"
              (click)="changeType()"
            >
              {{ 'change' | i18nextCap }}
            </button>
          </mat-form-field>
        } @else {
          <mat-form-field>
            <mat-label>{{ 'assetType' | i18nextCap }}</mat-label>
            <mat-select formControlName="assetType" [compareWith]="idEquals">
              @for (atype of assetTypeChoices; track atype.id) {
                <mat-option [value]="atype">
                  <ng-container
                    *ngTemplateOutlet="typeLabel; context: { $implicit: atype }"
                  />
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        <div class="name-row">
          <mat-form-field>
            <mat-label>{{ 'name' | i18nextCap }}</mat-label>
            <input matInput formControlName="name" />
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'externalId' | i18nextCap }}</mat-label>
            <input matInput formControlName="externalId" />
          </mat-form-field>
        </div>
        @if (showActive) {
          <mat-slide-toggle color="primary" formControlName="active">
            {{ 'active' | i18nextCap }}
          </mat-slide-toggle>
        }
        @if (createdAt != null) {
          <mat-form-field>
            <mat-label>{{ 'createdAt' | i18nextCap }}</mat-label>
            <input
              disabled
              matInput
              value="{{
                createdAt | momentTimezoneToAngularTimezone: 'DD/MM/YYYY HH:mm'
              }}"
            />
          </mat-form-field>
        }
        <div class="field-grid">
          <mat-form-field>
            <mat-label>{{ 'criticality' | i18nextCap }}</mat-label>
            <mat-select formControlName="criticality">
              <mat-option>{{ 'noneCriticality' | i18nextCap }}</mat-option>
              @for (c of criticalities; track c) {
                <mat-option [value]="c">{{ c | i18nextCap }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <div class="field-with-plus">
            <mat-form-field>
              <mat-label>{{ 'category' | i18nextCap }}</mat-label>
              <fo-autocompleting-select
                [choices]="categoryChoices$"
                valueField="id"
                formControlName="categoryId"
              />
            </mat-form-field>
            @if (canCreateCategory && !disabled) {
              <fo-plus-button (click)="createCategory()" />
            }
          </div>
        </div>
        <mat-form-field>
          <mat-label>{{ 'description' | i18nextCap }}</mat-label>
          <textarea matInput formControlName="description"></textarea>
        </mat-form-field>
      </div>
    </fo-form-section>

    <fo-form-section
      [title]="'ownershipAndLocation' | i18nextCap"
      [description]="'ownershipAndLocationDescription' | i18nextCap"
      icon="location_on"
      sectionId="ownershipAndLocation"
      [locked]="isLocked('ownershipAndLocation')"
      [status]="sectionStatus('ownershipAndLocation')"
      [errorCount]="sectionErrors('ownershipAndLocation')"
      [showNext]="hasNext('ownershipAndLocation')"
      (next)="openNext('ownershipAndLocation')"
    >
      <div class="section-body" [formGroup]="baseFormGroup">
        @if (showCompany) {
          <div class="company-field-row">
            <mat-form-field>
              <mat-label>{{ 'company' | i18nextCap }}</mat-label>
              <fo-autocompleting-select
                formControlName="companyId"
                valueField="id"
                [choices]="companyChoices$"
              />
              <fo-arrow matSuffix />
            </mat-form-field>
            <button
              mat-icon-button
              color="primary"
              [disabled]="baseFormGroup.value.companyId == null"
              [routerLink]="[
                '/',
                'companies',
                baseFormGroup.value.companyId,
                'edit',
              ]"
            >
              <mat-icon>account_balance</mat-icon>
            </button>
          </div>
        }
        <div class="field-grid">
          <mat-form-field>
            <mat-label>{{ 'location' | i18nextCap }}</mat-label>
            <mat-select formControlName="location" [compareWith]="idEquals">
              @for (loc of locations$ | async; track loc.id) {
                <mat-option [value]="loc">{{ loc.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'parent' | i18nextCap }}</mat-label>
            <fo-autocompleting-select
              [choices]="parentChoices$"
              valueField="id"
              formControlName="parentId"
            />
            <fo-arrow matSuffix />
          </mat-form-field>
        </div>
      </div>
    </fo-form-section>

    @if (assetClass === 'STANDALONE_EQUIPMENT') {
      <fo-form-section
        [title]="'equipmentDetails' | i18nextCap"
        [description]="'equipmentDetailsDescription' | i18nextCap"
        icon="precision_manufacturing"
        sectionId="equipmentDetails"
        [locked]="isLocked('equipmentDetails')"
        [status]="sectionStatus('equipmentDetails')"
        [errorCount]="sectionErrors('equipmentDetails')"
        [showNext]="hasNext('equipmentDetails')"
        (next)="openNext('equipmentDetails')"
      >
        <div class="section-body" [formGroup]="equipmentFormGroup">
          <div class="field-grid">
            <mat-form-field>
              <mat-label>{{ 'brand' | i18nextCap }}</mat-label>
              <input matInput formControlName="brand" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'model' | i18nextCap }}</mat-label>
              <input matInput formControlName="model" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'serialNumber' | i18nextCap }}</mat-label>
              <input matInput formControlName="serialNumber" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'type' | i18nextCap }}</mat-label>
              <input matInput formControlName="equipmentType" />
            </mat-form-field>
          </div>
          <mat-form-field class="date-picker">
            <mat-label>{{ 'manufacturedAt' | i18nextCap }}</mat-label>
            <input
              matInput
              formControlName="manufacturedAt"
              [matDatepicker]="manufacturedDatePicker"
            />
            <mat-datepicker-toggle matSuffix [for]="manufacturedDatePicker" />
            <mat-datepicker #manufacturedDatePicker />
          </mat-form-field>
        </div>
      </fo-form-section>
    }

    <fo-form-section
      [title]="'installationAndWarranty' | i18nextCap"
      [description]="'installationAndWarrantyDescription' | i18nextCap"
      icon="event_available"
      sectionId="installationAndWarranty"
      [locked]="isLocked('installationAndWarranty')"
      [status]="sectionStatus('installationAndWarranty')"
      [errorCount]="sectionErrors('installationAndWarranty')"
      [showNext]="hasNext('installationAndWarranty')"
      (next)="openNext('installationAndWarranty')"
    >
      <div class="section-body" [formGroup]="baseFormGroup">
        <mat-slide-toggle color="primary" formControlName="hasWarranty">{{
          'hasWarranty' | i18nextCap
        }}</mat-slide-toggle>
        @if (baseFormGroup.controls.hasWarranty.value) {
          <mat-form-field class="date-picker">
            <mat-label>{{ 'warrantyExpiresAt' | i18nextCap }}</mat-label>
            <input
              matInput
              formControlName="warranty"
              [matDatepicker]="datePicker"
            />
            <mat-datepicker-toggle matSuffix [for]="datePicker" />
            <mat-datepicker #datePicker />
          </mat-form-field>
        }
        <mat-form-field class="date-picker">
          <mat-label>{{ 'installedAt' | i18nextCap }}</mat-label>
          <input
            matInput
            formControlName="installedAt"
            [matDatepicker]="installedDatePicker"
          />
          <mat-datepicker-toggle matSuffix [for]="installedDatePicker" />
          <mat-datepicker #installedDatePicker />
        </mat-form-field>
      </div>
    </fo-form-section>

    @if (customFields.length > 0) {
      <fo-form-section
        [title]="'customFieldsSection' | i18nextCap"
        [description]="'customFieldsSectionDescription' | i18nextCap"
        icon="tune"
        sectionId="customFieldsSection"
        [locked]="isLocked('customFieldsSection')"
        [status]="sectionStatus('customFieldsSection')"
        [errorCount]="sectionErrors('customFieldsSection')"
        [showNext]="hasNext('customFieldsSection')"
        (next)="openNext('customFieldsSection')"
      >
        <div class="section-body" [formGroup]="customFormGroup">
          <div class="custom-fields" formArrayName="fields">
            @for (field of customFields; track $index) {
              @switch (field.type) {
                @case ('TEXT') {
                  <mat-form-field>
                    <mat-label>{{ field.name }}</mat-label>
                    <input matInput type="text" [formControlName]="$index" />
                    @if (fieldHint(field); as hint) {
                      <mat-hint>{{ hint }}</mat-hint>
                    }
                  </mat-form-field>
                }
                @case ('INTEGER') {
                  <mat-form-field>
                    <mat-label>{{ field.name }}</mat-label>
                    <input
                      matInput
                      foNumericInput="integer"
                      [formControlName]="$index"
                    />
                    @if (field.unit) {
                      <span matTextSuffix>{{ field.unit }}</span>
                    }
                    @if (fieldHint(field); as hint) {
                      <mat-hint>{{ hint }}</mat-hint>
                    }
                    @if (
                      customFormGroup.controls.fields
                        .at($index)
                        .hasError('numeric')
                    ) {
                      <mat-error>{{ 'invalidInteger' | i18nextCap }}</mat-error>
                    } @else if (
                      customFormGroup.controls.fields
                        .at($index)
                        .hasError('outOfRange')
                    ) {
                      <mat-error
                        >{{ 'valueOutOfRange' | i18nextCap }}
                        {{ fieldLimits(field) }}</mat-error
                      >
                    }
                  </mat-form-field>
                }
                @case ('REAL') {
                  <mat-form-field>
                    <mat-label>{{ field.name }}</mat-label>
                    <input
                      matInput
                      foNumericInput="decimal"
                      [formControlName]="$index"
                    />
                    @if (field.unit) {
                      <span matTextSuffix>{{ field.unit }}</span>
                    }
                    @if (fieldHint(field); as hint) {
                      <mat-hint>{{ hint }}</mat-hint>
                    }
                    @if (
                      customFormGroup.controls.fields
                        .at($index)
                        .hasError('numeric')
                    ) {
                      <mat-error>{{ 'invalidDecimal' | i18nextCap }}</mat-error>
                    } @else if (
                      customFormGroup.controls.fields
                        .at($index)
                        .hasError('outOfRange')
                    ) {
                      <mat-error
                        >{{ 'valueOutOfRange' | i18nextCap }}
                        {{ fieldLimits(field) }}</mat-error
                      >
                    }
                  </mat-form-field>
                }
                @case ('BOOLEAN') {
                  <div class="custom-field-with-hint">
                    <mat-slide-toggle
                      color="primary"
                      [formControlName]="$index"
                      >{{ field.name }}</mat-slide-toggle
                    >
                    @if (fieldHint(field); as hint) {
                      <span class="custom-field-hint">{{ hint }}</span>
                    }
                  </div>
                }
                @case ('DATE') {
                  <mat-form-field class="date-picker">
                    <mat-label>{{ field.name }}</mat-label>
                    <input
                      matInput
                      [formControlName]="$index"
                      [matDatepicker]="customDatePicker"
                    />
                    <mat-datepicker-toggle matSuffix [for]="customDatePicker" />
                    <mat-datepicker #customDatePicker />
                    @if (fieldHint(field); as hint) {
                      <mat-hint>{{ hint }}</mat-hint>
                    }
                  </mat-form-field>
                }
                @case ('TIMESTAMP') {
                  <div class="custom-field-with-hint">
                    <fo-date-time-input
                      [mainLabel]="field.name"
                      [mainLabelLiteral]="true"
                      [formControlName]="$index"
                    />
                    @if (fieldHint(field); as hint) {
                      <span class="custom-field-hint">{{ hint }}</span>
                    }
                  </div>
                }
                @case ('ENUM') {
                  <mat-form-field>
                    <mat-label>{{ field.name }}</mat-label>
                    <mat-select [formControlName]="$index">
                      <mat-option [value]="''">{{
                        'noneGeneric' | i18nextCap
                      }}</mat-option>
                      @for (opt of field.enumChoices; track opt) {
                        <mat-option [value]="opt">{{ opt }}</mat-option>
                      }
                    </mat-select>
                    @if (fieldHint(field); as hint) {
                      <mat-hint>{{ hint }}</mat-hint>
                    }
                  </mat-form-field>
                }
              }
            }
          </div>
        </div>
      </fo-form-section>
    }

    <fo-form-section
      [title]="'attachments' | i18nextCap"
      [description]="'attachmentsDescription' | i18nextCap"
      icon="attach_file"
      sectionId="attachments"
      [locked]="isLocked('attachments')"
      [status]="sectionStatus('attachments')"
      [errorCount]="sectionErrors('attachments')"
      [showNext]="hasNext('attachments')"
      (next)="openNext('attachments')"
    >
      <div class="section-body">
        <fo-file-upload
          [acceptedFileTypes]="acceptedFileTypes"
          [disabled]="disabled"
          [attachments]="attachments"
          (newAttachmentsChange)="newAttachmentsChange.emit($event)"
        />
      </div>
    </fo-form-section>
  </fo-sectioned-form>
}
